import React, { useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { submitContactForm } from '../../util/api';
import { leadGenerated } from '../../analytics/analytics';

import { H1, H2, Page, LayoutSingleColumn } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import ContactForm from './ContactForm/ContactForm';
import css from './ContactPage.module.css';

// Canal por defecto cuando la visita no trae utm_source. Con mayúscula, tal
// cual lo espera el CRM.
const DEFAULT_CANAL = 'Web';

// Límites de las UTM. Mismos valores que MAX_LENGTHS en server/api/contact.js.
const UTM_MAX_LENGTH = {
  canal: 64,
  way: 64,
  campaign: 128,
};

/**
 * Recorta espacios y limita longitud. NO toca mayúsculas/minúsculas: el valor
 * de la UTM viaja al CRM tal como venía.
 */
const sanitizeUtm = (value, maxLength) =>
  typeof value === 'string' ? value.trim().slice(0, maxLength) : '';

/**
 * Lee utm_source / utm_medium / utm_campaign de la query string y los traduce a
 * los nombres que espera el CRM (canal / way / campaign).
 *
 * Se llama UNA VEZ al montar la página (inicializador de useState), no en el
 * submit: así las UTM sobreviven aunque el usuario navegue por la página o
 * limpie la URL antes de enviar el formulario.
 */
const parseUtmParams = search => {
  const params = new URLSearchParams(search || '');

  return {
    canal: sanitizeUtm(params.get('utm_source'), UTM_MAX_LENGTH.canal) || DEFAULT_CANAL,
    way: sanitizeUtm(params.get('utm_medium'), UTM_MAX_LENGTH.way),
    campaign: sanitizeUtm(params.get('utm_campaign'), UTM_MAX_LENGTH.campaign),
  };
};

// Códigos de error que devuelve server/api/contact.js → clave de traducción.
const ERROR_MESSAGE_IDS = {
  'validation-failed': 'ContactPage.errorValidation',
  'not-configured': 'ContactPage.errorNotConfigured',
  'crm-error': 'ContactPage.errorGeneric',
  'request-failed': 'ContactPage.errorGeneric',
};

// Solo dos tarjetas: son las dos situaciones paralelas del usuario. Lo que
// antes era la tercera ("Cómo trabajamos") no es una opción más sino una
// aclaración que aplica a ambas, y ahora vive en la banda de cierre.
const HELP_BLOCKS = ['selling', 'buying'];

// Bullets de la columna izquierda del hero.
const HERO_BULLETS = ['free', 'anonymous', 'help'];

// Tick de los bullets. El color lo pone .bulletIcon vía currentColor
// (--uroboroxGreen400, el acento del sitio).
const CheckIcon = () => (
  <svg
    className={css.bulletIcon}
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M3.75 9.5l3.25 3.25 7.25-7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ContactPageComponent = props => {
  const { scrollingDisabled, onLeadGenerated } = props;
  const intl = useIntl();
  const location = useLocation();

  // UTM capturadas al montar. El inicializador de useState corre una sola vez.
  const [utmParams] = useState(() => parseUtmParams(location.search));

  // 'idle' | 'submitting' | 'success' | 'error'
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = values => {
    // Un solo envío en curso a la vez, y ninguno más una vez ha ido bien.
    if (status === 'submitting' || status === 'success') {
      return;
    }

    setStatus('submitting');
    setErrorMessage(null);

    // El checkbox de final-form es un array (['yes'] marcado, [] sin marcar).
    const consent = Array.isArray(values.consent) && values.consent.length > 0;

    const body = {
      email: values.email,
      phone: values.phone,
      consulta: values.consulta,
      consent,
      website: values.website, // honeypot
      ...utmParams, // canal, way, campaign
    };

    return submitContactForm(body)
      .then(() => {
        setStatus('success');
        // GA4: generate_lead solo en éxito real (request() de util/api.js lanza
        // si el status es >= 400). Una sola vez por envío: la guarda de arriba
        // corta si el estado ya es 'submitting' o 'success', y tras el éxito el
        // formulario desaparece y lo sustituye el estado de gracias.
        // Sin parámetros: las UTM ya las recoge GA4 por su cuenta. utmParams
        // sigue viajando en el POST al CRM, que sí las necesita.
        onLeadGenerated();
      })
      .catch(e => {
        const messageId = ERROR_MESSAGE_IDS[e?.code] || 'ContactPage.errorGeneric';
        setErrorMessage(intl.formatMessage({ id: messageId }));
        setStatus('error');
      });
  };

  const schemaTitle = intl.formatMessage({ id: 'ContactPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'ContactPage.schemaDescription' });

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      scrollingDisabled={scrollingDisabled}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ContactPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {/* Banda hero a ancho completo sobre emerald950. Dos columnas en
            escritorio; en móvil se apilan y el formulario queda debajo del
            copy (orden natural del DOM). */}
        <section className={css.hero}>
          <div className={css.heroInner}>
            <div className={css.heroCopy}>
              <H1 className={css.title}>
                <FormattedMessage id="ContactPage.title" />
              </H1>
              <p className={css.intro}>
                <FormattedMessage id="ContactPage.intro" />
              </p>
            </div>

            {/* Los bullets son hermanos del formulario, no hijos de .heroCopy:
                así la grid puede colocarlos DESPUÉS de la tarjeta en móvil y
                debajo del copy en escritorio. Ver grid-template-areas. */}
            <ul className={css.bullets}>
              {HERO_BULLETS.map(bullet => (
                <li key={bullet} className={css.bullet}>
                  <CheckIcon />
                  <span>
                    <FormattedMessage id={`ContactPage.bullet-${bullet}`} />
                  </span>
                </li>
              ))}
            </ul>

            <div className={css.heroAside}>
              {/* Tarjeta blanca. El estado de gracias sustituye al formulario
                  DENTRO de la tarjeta: misma página, sin cambio de ruta. */}
              <div className={css.card}>
                {status === 'success' ? (
                  <div className={css.thanks} role="status">
                    <H2 className={css.thanksTitle}>
                      <FormattedMessage id="ContactPage.thanksTitle" />
                    </H2>
                    <p className={css.thanksMessage}>
                      <FormattedMessage id="ContactPage.thanksMessage" />
                    </p>
                  </div>
                ) : (
                  <ContactForm
                    formId="ContactForm"
                    onSubmit={handleSubmit}
                    inProgress={status === 'submitting'}
                    sendError={errorMessage}
                  />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Bloque inferior: banda gris clara a ancho completo, con encabezado
            de sección, dos tarjetas oscuras y la nota de cierre. */}
        <section className={css.blocks}>
          <div className={css.blocksInner}>
            {/* <h2> plano, no el componente H2, igual que LandingPage: H2
                aplica .h2 (composes h2 from global), que es una clase (0,1,0)
                y competiría en especificidad con .sectionTitle dejando el
                resultado a merced del orden de la cascada. */}
            <header className={css.sectionHeader}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage id="ContactPage.blocksTitle" />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage id="ContactPage.blocksSubtitle" />
              </p>
            </header>

            <div className={css.blocksGrid}>
              {HELP_BLOCKS.map(block => (
                <article key={block} className={css.block}>
                  <h3 className={css.blockTitle}>
                    <FormattedMessage id={`ContactPage.${block}Title`} />
                  </h3>
                  <p className={css.blockText}>
                    <FormattedMessage id={`ContactPage.${block}Text`} />
                  </p>
                </article>
              ))}
            </div>

            {/* Nota de cierre: aclaración que aplica a las dos tarjetas. Texto
                suelto dentro de la misma sección gris — sin fondo, sin borde y
                sin caja propia. */}
            <p className={css.note}>
              <FormattedMessage
                id="ContactPage.closingNote"
                values={{ b: chunks => <strong className={css.noteLead}>{chunks}</strong> }}
              />
            </p>
          </div>
        </section>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const mapDispatchToProps = dispatch => ({
  // Señal de analítica: la recoge el middleware de src/analytics/analytics.js
  // y avisa a los handlers registrados. Los componentes no hablan con gtag.
  onLeadGenerated: () => dispatch(leadGenerated()),
});

const ContactPage = compose(connect(mapStateToProps, mapDispatchToProps))(ContactPageComponent);

export default ContactPage;
